import { theme } from 'antd';

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as TooltipRC,
  XAxis,
  YAxis
} from 'recharts';
import type { TickItem } from 'recharts/types/util/types';
import type { ILineChartData } from 'v2/interfaces/dashboard';

import { SERIES_BG_COLOR_CLASS, type LineChartSeriesKey } from '../seriesColors';
import CustomTooltip from './CustomTooltip';

interface IProps {
  data: ILineChartData[];
  totalGains: number;
}

interface ILabelProps {
  payload?: TickItem;
  y?: number;
  x?: number;
  fill?: string;
}

interface ILegendProps {
  payload?: any[];
}

const LegendContent = ({ payload }: ILegendProps) => {
  const visibleEntries = payload?.filter(entry => entry.type !== 'none') ?? [];

  return (
    <ul className='mt-0 mb-4 ml-0 flex list-none items-center justify-start'>
      {visibleEntries.map(entry => (
        <li className='mr-4 flex items-center text-base' key={entry.value}>
          <span className={`mr-1 block h-0.5 w-4 ${SERIES_BG_COLOR_CLASS[entry.dataKey as LineChartSeriesKey]}`} />
          {entry.value}
        </li>
      ))}
    </ul>
  );
};

const DatePick = ({ payload, y, x, fill, ...rest }: ILabelProps) => {
  return (
    <>
      <text y={y + 15} x={x - 15} {...rest} fill={fill} fontSize={9}>
        {payload.value}
      </text>
    </>
  );
};

const Chart = ({ data, totalGains }: IProps) => {
  const { token } = theme.useToken();
  const isNegativeGains = totalGains < 0;
  const seriesColors = {
    producer: token.colorWarning,
    coproducer: token.colorWarningActive,
    affiliate: token.colorPrimary
  };

  return (
    <ResponsiveContainer width='100%' height={250}>
      <ComposedChart
        data={data}
        margin={{
          left: -7,
          right: 16,
          bottom: 11
        }}
      >
        <Legend offset={16} verticalAlign='top' content={<LegendContent />} />
        <Area
          type='monotone'
          dataKey='producer'
          fill={token.colorWarningBg}
          stroke='none'
          legendType='none'
          tooltipType='none'
          isAnimationActive={false}
        />
        <Line
          type='monotone'
          dataKey='producer'
          name='Produtor'
          stroke={seriesColors.producer}
          fill={seriesColors.producer}
          strokeWidth={2}
          dot={{ fill: token.colorBgContainer, strokeWidth: 2, r: 3 }}
        />
        <Line
          type='monotone'
          dataKey='coproducer'
          name='Coprodutor'
          stroke={seriesColors.coproducer}
          fill={seriesColors.coproducer}
          strokeWidth={2}
          dot={{ fill: token.colorBgContainer, strokeWidth: 2, r: 3 }}
        />
        <Line
          type='monotone'
          dataKey='affiliate'
          name='Afiliado'
          stroke={seriesColors.affiliate}
          fill={seriesColors.affiliate}
          strokeWidth={2}
          dot={{ fill: token.colorBgContainer, strokeWidth: 2, r: 3 }}
        />
        <CartesianGrid vertical={false} stroke={token.colorBorderSecondary} />
        <XAxis
          dataKey='date'
          fontSize={9}
          interval={data.length < 10 && 0}
          tick={<DatePick fill={token.colorTextTertiary} />}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type='number'
          allowDecimals={false}
          interval='preserveStartEnd'
          fontSize={9}
          domain={[0, isNegativeGains ? Math.abs(totalGains) : 'auto']}
          axisLine={false}
          tickLine={false}
        />
        <TooltipRC content={<CustomTooltip />} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default Chart;
